import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../config/api';
import toast from 'react-hot-toast';
import { FaMapMarkerAlt, FaClock, FaUser, FaStar, FaCheck, FaTimes, FaCreditCard, FaComments, FaMoneyBillWave } from 'react-icons/fa';
import loadRazorpay from '../utils/razorpay';
import ChatWindow from '../components/ChatWindow';
import RatingModal from '../components/RatingModal';
import { cleanAddress } from '../utils/addressUtils';
import usePushRefresh from '../hooks/usePushRefresh';

const MyBookings = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [processingPayment, setProcessingPayment] = useState(null);
  const [openChat, setOpenChat] = useState(null);
  const [openRating, setOpenRating] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(null);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('razorpay');
  const { user } = useAuth();

  const fetchBookings = useCallback(async () => {
    try {
      const params = statusFilter !== 'all' ? { status: statusFilter } : {};
      const response = await api.get('/api/bookings/my-bookings', { params });
      // Ensure bookings is always an array
      const bookingsData = response.data?.bookings || response.data || [];
      setBookings(Array.isArray(bookingsData) ? bookingsData : []);
    } catch (error) {
      console.error('Fetch bookings error:', error);
      toast.error('Failed to load bookings');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  usePushRefresh(() => {
    fetchBookings();
  });

  useEffect(() => {
    const refreshOnFocus = () => {
      if (!document.hidden) {
        fetchBookings();
      }
    };

    document.addEventListener('visibilitychange', refreshOnFocus);
    window.addEventListener('focus', fetchBookings);

    return () => {
      document.removeEventListener('visibilitychange', refreshOnFocus);
      window.removeEventListener('focus', fetchBookings);
    };
  }, [fetchBookings]);

  const handleCancelBooking = async (bookingId) => {
    if (!window.confirm('Are you sure you want to cancel this booking?')) return;

    try {
      await api.put(`/api/bookings/${bookingId}/cancel`);
      toast.success('Booking cancelled successfully');
      fetchBookings();
    } catch (error) {
      console.error('Cancel booking error:', error);
      toast.error('Failed to cancel booking');
    }
  };

  const handlePaymentMethodSelect = (booking) => {
    setShowPaymentModal(booking);
    setSelectedPaymentMethod('razorpay');
  };

  const handlePayment = async (booking, paymentMethod = 'razorpay') => {
    if (processingPayment) return;
    
    setProcessingPayment(booking._id);
    setShowPaymentModal(null);
    
    try {
      // Update payment method if cash is selected
      if (paymentMethod === 'cash') {
        await api.put(`/api/payments/update-method/${booking.payment?._id || booking._id}`, {
          paymentMethod: 'cash'
        });
        toast.success(`Cash payment selected. Please pay ₹${booking.amount} to the rider after the ride.`);
        setProcessingPayment(null);
        fetchBookings();
        return;
      }

      // Load Razorpay script for online payment
      await loadRazorpay();
      
      if (!window.Razorpay) {
        throw new Error('Payment gateway not available');
      }

      // Get Razorpay key from backend
      const configResponse = await api.get('/api/payments/config');
      const razorpayKey = configResponse.data.keyId;

      // Update payment method to razorpay before creating order
      // This ensures payment method is set before status changes to processing
      try {
        await api.put(`/api/payments/update-method/${booking.payment?._id || booking._id}`, {
          paymentMethod: 'razorpay'
        });
      } catch (updateError) {
        // If update fails, continue anyway (might already be correct)
        console.warn('Payment method update warning:', updateError.response?.data?.message);
      }

      // Create order
      const orderResponse = await api.post('/api/payments/create-order', {
        bookingId: booking._id,
        amount: booking.amount
      });

      const { order, paymentId, isTestMode } = orderResponse.data;

      // Check if this is a test order (no real Razorpay credentials)
      if (isTestMode || order.id?.startsWith('order_test_') || razorpayKey === 'rzp_test_key' || !razorpayKey || razorpayKey.includes('your_razor')) {
        // Test mode - simulate payment success
        toast('Test mode: Simulating payment...', { 
          icon: 'ℹ️',
          duration: 2000 
        });
        
        try {
          // Simulate payment verification
          await api.post('/api/payments/verify', {
            paymentId: `pay_test_${Date.now()}`,
            orderId: order.id,
            signature: `sig_test_${Date.now()}`,
            bookingId: booking._id
          });

          toast.success('Payment successful! (Test Mode)');
          fetchBookings();
        } catch (error) {
          console.error('Payment verification error:', error);
          // For test mode, mark as paid anyway
          toast.success('Payment processed (Test Mode)');
          fetchBookings();
        } finally {
          setProcessingPayment(null);
        }
        return;
      }

      // Real Razorpay mode - initialize checkout
      const options = {
        key: razorpayKey,
        amount: order.amount,
        currency: order.currency,
        name: 'SastaMastaRide',
        description: `Payment for ride booking`,
        order_id: order.id,
        handler: async function(response) {
          try {
            // Verify payment
            await api.post('/api/payments/verify', {
              paymentId: response.razorpay_payment_id,
              orderId: response.razorpay_order_id,
              signature: response.razorpay_signature,
              bookingId: booking._id
            });

            toast.success('Payment successful!');
            fetchBookings();
          } catch (error) {
            console.error('Payment verification error:', error);
            toast.error(error.response?.data?.message || 'Payment verification failed');
          } finally {
            setProcessingPayment(null);
          }
        },
        prefill: {
          name: user?.name || '',
          email: user?.email || '',
          contact: user?.phone || ''
        },
        theme: {
          color: '#3B82F6'
        },
        modal: {
          ondismiss: function() {
            setProcessingPayment(null);
          }
        }
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();
      
    } catch (error) {
      console.error('Payment error:', error);
      toast.error(error.response?.data?.message || 'Failed to initiate payment');
      setProcessingPayment(null);
    }
  };

  const getStatusBadge = (status) => {
    const statusClasses = {
      pending: 'badge-warning',
      accepted: 'badge-success',
      rejected: 'badge-danger',
      cancelled: 'badge-danger',
      completed: 'badge-success'
    };
    
    return (
      <span className={`badge ${statusClasses[status] || 'badge-secondary'}`}>
        {status.toUpperCase()}
      </span>
    );
  };

  const getPaymentStatusBadge = (status, paymentMethod) => {
    const statusClasses = {
      pending: 'badge-warning',
      paid: 'badge-success',
      refunded: 'badge-info',
      failed: 'badge-danger'
    };
    
    const methodLabel = paymentMethod === 'cash' ? ' (Cash)' : '';
    
    return (
      <span className={`badge ${statusClasses[status] || 'badge-secondary'}`}>
        {status.toUpperCase()}{methodLabel}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-8">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-800">My Bookings</h1>
            <a href="/" className="btn-primary w-full sm:w-auto text-center">
              Find New Rides
            </a>
          </div>

          {/* Filter */}
          <div className="mb-6">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="input-field w-full sm:w-auto"
            >
              <option value="all">All Bookings</option>
              <option value="pending">Pending</option>
              <option value="accepted">Accepted</option>
              <option value="rejected">Rejected</option>
              <option value="cancelled">Cancelled</option>
              <option value="completed">Completed</option>
            </select>
          </div>

          {/* Bookings List */}
          {bookings && Array.isArray(bookings) && bookings.length > 0 ? (
            <div className="space-y-4">
              {bookings.map((booking) => (
                <div key={booking._id} className="card">
                  <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-4">
                    <div className="flex-1 w-full">
                      {/* Rider Info */}
                      <div className="flex items-center mb-4">
                        <img
                          src={booking.rider?.photo || '/default-avatar.png'}
                          alt={booking.rider?.name}
                          className="w-12 h-12 rounded-full mr-4"
                        />
                        <div>
                          <h3 className="font-semibold text-gray-800">{booking.rider?.name}</h3>
                          <div className="flex items-center text-sm text-gray-600">
                            <FaStar className="text-yellow-400 mr-1" />
                            <span>{booking.rider?.rating?.toFixed(1)}</span>
                            <span className="mx-2">•</span>
                            <span>{booking.rider?.totalRides} rides</span>
                          </div>
                        </div>
                      </div>

                      {/* Route */}
                      <div className="mb-4">
                        <div className="flex items-center mb-2">
                          <FaMapMarkerAlt className="text-green-500 mr-2" />
                          <span className="font-medium">{cleanAddress(booking.trip?.origin.address)}</span>
                        </div>
                        <div className="flex items-center">
                          <FaMapMarkerAlt className="text-red-500 mr-2" />
                          <span className="font-medium">{cleanAddress(booking.trip?.destination.address)}</span>
                        </div>
                      </div>

                      {/* Trip Details */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4 mb-4">
                        <div className="flex items-center text-gray-600">
                          <FaClock className="mr-2" />
                          <span>{new Date(booking.trip?.departureTime).toLocaleString()}</span>
                        </div>
                        <div className="text-gray-600">
                          <span className="font-semibold">₹{booking.amount}</span>
                        </div>
                        <div className="text-gray-600">
                          Booked: {new Date(booking.createdAt).toLocaleDateString()}
                        </div>
                      </div>

                      {/* Status */}
                      <div className="flex space-x-2 mb-4">
                        {getStatusBadge(booking.status)}
                        {getPaymentStatusBadge(booking.paymentStatus, booking.payment?.paymentMethod)}
                      </div>

                      {/* OTP Display for Passengers */}
                      {booking.status === 'accepted' && booking.pickupOTP && booking.trip?.status === 'booked' && (
                        <div className="mb-4 p-4 bg-gradient-to-r from-blue-50 to-purple-50 border-2 border-blue-300 rounded-lg shadow-md">
                          <div className="text-center">
                            <div className="text-sm font-semibold text-blue-900 mb-2">
                              Your Pickup OTP
                            </div>
                            <div className="text-4xl font-bold text-blue-600 mb-2 tracking-wider">
                              {booking.pickupOTP}
                            </div>
                            <div className="text-xs text-blue-700 mb-2">
                              Share this OTP with your rider when they arrive
                            </div>
                            {booking.otpGeneratedAt && (
                              <div className="text-xs text-gray-600">
                                Generated: {new Date(booking.otpGeneratedAt).toLocaleTimeString()}
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Payment Section - Cash or Online */}
                      {((booking.status === 'accepted' && booking.paymentStatus === 'pending') || 
                        (booking.status === 'completed' && booking.paymentStatus === 'pending')) && (
                        <div className="mb-4">
                          {booking.payment?.paymentMethod === 'cash' ? (
                            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                              <div className="flex items-start space-x-3">
                                <FaMoneyBillWave className="text-blue-600 text-xl flex-shrink-0 mt-1" />
                                <div className="flex-1">
                                  <h4 className="font-semibold text-blue-900 mb-1">Cash Payment Selected</h4>
                                  <p className="text-blue-800 text-sm">
                                    You will pay ₹{booking.amount} in cash to the rider after the ride.
                                  </p>
                                  <p className="text-blue-700 text-xs mt-2">
                                    Payment will be confirmed by the rider once they receive the cash.
                                  </p>
                                </div>
                              </div>
                            </div>
                          ) : (
                            <div className="overflow-x-auto overflow-y-hidden scrollbar-hide -mx-4 px-4 md:mx-0 md:px-0" style={{ WebkitOverflowScrolling: 'touch' }}>
                              <div className="flex space-x-3 min-w-max md:min-w-0 md:flex-wrap md:space-x-2 md:space-y-2">
                                <button
                                  onClick={() => handlePaymentMethodSelect(booking)}
                                  disabled={processingPayment === booking._id}
                                  className="flex items-center justify-center space-x-2 px-6 py-3 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap shadow-md"
                                >
                                  <FaCreditCard className="text-lg flex-shrink-0" />
                                  <span className="font-medium">
                                    {processingPayment === booking._id ? 'Processing...' : `Pay ₹${booking.amount}`}
                                  </span>
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Payment Status - Paid */}
                      {booking.paymentStatus === 'paid' && (
                        <div className="mb-4 p-3 bg-green-50 rounded-lg">
                          <div className="flex items-center space-x-2 text-green-700">
                            <FaCheck className="text-lg" />
                            <span className="font-semibold">
                              Payment Completed - ₹{booking.amount}
                              {booking.payment?.paymentMethod === 'cash' && ' (Cash)'}
                            </span>
                          </div>
                        </div>
                      )}

                      {/* Notes */}
                      {booking.notes && (
                        <div className="mb-4">
                          <h4 className="font-semibold text-gray-800 mb-1">Notes</h4>
                          <p className="text-gray-600 text-sm">{booking.notes}</p>
                        </div>
                      )}

                      {/* Ratings */}
                      {booking.status === 'completed' && (
                        <div className="mb-4">
                          {booking.passengerRating?.rating ? (
                            <div className="bg-blue-50 p-4 rounded-lg">
                              <div className="flex items-center space-x-2 mb-2">
                                <span className="text-sm font-semibold text-gray-800">Your Rating:</span>
                                <div className="flex items-center space-x-1">
                                  {[1, 2, 3, 4, 5].map((star) => (
                                    <FaStar
                                      key={star}
                                      className={`text-sm ${
                                        star <= booking.passengerRating.rating
                                          ? 'text-yellow-400'
                                          : 'text-gray-300'
                                      }`}
                                    />
                                  ))}
                                  <span className="text-sm text-gray-600 ml-1">
                                    ({booking.passengerRating.rating}/5)
                                  </span>
                                </div>
                              </div>
                              {booking.passengerRating.review && (
                                <div className="text-sm text-gray-700 italic mt-2">
                                  "{booking.passengerRating.review}"
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                              <div className="flex items-center justify-between">
                                <div>
                                  <p className="text-sm font-semibold text-gray-800 mb-1">
                                    Rate Your Experience
                                  </p>
                                  <p className="text-xs text-gray-600">
                                    Help us improve by rating your ride
                                  </p>
                                </div>
                                <button
                                  onClick={() => setOpenRating(booking._id)}
                                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium flex items-center space-x-2"
                                >
                                  <FaStar />
                                  <span>Rate Now</span>
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="ml-0 mt-4 md:ml-6 md:mt-0 flex flex-col space-y-2">
                      {/* Chat Button - Available for all bookings */}
                      {(booking.status === 'pending' || booking.status === 'accepted' || booking.status === 'completed') && (
                        <button
                          onClick={() => setOpenChat(booking._id)}
                          className="btn-secondary text-sm w-full md:w-auto flex items-center justify-center space-x-2"
                        >
                          <FaComments />
                          <span>Chat</span>
                        </button>
                      )}
                      
                      {booking.status === 'pending' && (
                        <button
                          onClick={() => handleCancelBooking(booking._id)}
                          className="btn-danger text-sm w-full md:w-auto"
                        >
                          Cancel Booking
                        </button>
                      )}
                      
                      {booking.status === 'accepted' && (
                        <div className="text-center">
                          <div className="text-sm text-green-600 mb-2">
                            <FaCheck className="inline mr-1" />
                            Booking Confirmed
                          </div>
                          <button
                            onClick={() => handleCancelBooking(booking._id)}
                            className="btn-danger text-sm w-full md:w-auto mb-2"
                          >
                            Cancel Booking
                          </button>
                          <a
                            href={`/track/${booking._id}`}
                            className="btn-primary text-sm block mb-2 w-full md:w-auto"
                          >
                            Track Trip
                          </a>
                        </div>
                      )}
                      
                      {booking.status === 'rejected' && (
                        <div className="text-center">
                          <div className="text-sm text-red-600 mb-2">
                            <FaTimes className="inline mr-1" />
                            Booking Rejected
                          </div>
                          <a
                            href="/"
                            className="btn-secondary text-sm w-full md:w-auto"
                          >
                            Find Another Ride
                          </a>
                        </div>
                      )}
                      
                      {booking.status === 'completed' && (
                        <div className="text-center">
                          <div className="text-sm text-green-600 mb-2">
                            <FaCheck className="inline mr-1" />
                            Trip Completed
                          </div>
                          <a
                            href={`/trip/${booking.trip?._id}`}
                            className="btn-secondary text-sm block mb-2 w-full md:w-auto"
                          >
                            View Details
                          </a>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <FaUser className="mx-auto text-gray-400 text-6xl mb-4" />
              <h3 className="text-xl font-semibold text-gray-600 mb-2">
                No bookings found
              </h3>
              <p className="text-gray-500 mb-6">
                {statusFilter === 'all' 
                  ? "You haven't booked any rides yet."
                  : `No bookings with status "${statusFilter}" found.`
                }
              </p>
              <a href="/" className="btn-primary">
                Find Your First Ride
              </a>
            </div>
          )}
        </div>
      </div>
      
      {/* Chat Window */}
      {openChat && (
        <ChatWindow
          bookingId={openChat}
          onClose={() => setOpenChat(null)}
          currentUser={user}
        />
      )}
      
      {/* Payment Method Selection Modal */}
      {showPaymentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-xl font-semibold text-gray-800 mb-4">
              Select Payment Method
            </h3>
            <p className="text-gray-600 mb-4">
              Amount to pay: <span className="font-bold text-green-600">₹{showPaymentModal.amount}</span>
            </p>
            
            <div className="space-y-3 mb-6">
              <label className="flex items-center p-4 border-2 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                <input
                  type="radio"
                  name="paymentMethod"
                  value="razorpay"
                  checked={selectedPaymentMethod === 'razorpay'}
                  onChange={(e) => setSelectedPaymentMethod(e.target.value)}
                  className="mr-3"
                />
                <FaCreditCard className="mr-3 text-blue-600 text-xl" />
                <div className="flex-1">
                  <div className="font-medium text-gray-800">Pay Online</div>
                  <div className="text-xs text-gray-500">Credit/Debit Card, UPI, Wallet</div>
                </div>
              </label>
              
              <label className="flex items-center p-4 border-2 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                <input
                  type="radio"
                  name="paymentMethod"
                  value="cash"
                  checked={selectedPaymentMethod === 'cash'}
                  onChange={(e) => setSelectedPaymentMethod(e.target.value)}
                  className="mr-3"
                />
                <FaMoneyBillWave className="mr-3 text-green-600 text-xl" />
                <div className="flex-1">
                  <div className="font-medium text-gray-800">Pay by Cash</div>
                  <div className="text-xs text-gray-500">Pay directly to rider after ride</div>
                </div>
              </label>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={() => setShowPaymentModal(null)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => handlePayment(showPaymentModal, selectedPaymentMethod)}
                disabled={processingPayment === showPaymentModal._id}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                {processingPayment === showPaymentModal._id ? 'Processing...' : 'Continue'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Rating Modal */}
      {openRating && (
        <RatingModal
          bookingId={openRating}
          onClose={() => setOpenRating(null)}
          onSuccess={() => fetchBookings()}
        />
      )}
    </div>
  );
};

export default MyBookings;
